import { reactive } from "vue";
import ProjectService, { PROJECT_CONSTANT } from "../services/projectService.js";

const projectService = new ProjectService();

const TAB_KEYS = [
  PROJECT_CONSTANT.PROJECT_TYPES.ALL,
  PROJECT_CONSTANT.PROJECT_TYPES.PERSONAL,
  PROJECT_CONSTANT.PROJECT_TYPES.SHARED_WITH_ME,
];

// The backend has no real per-type query — "Personal" vs "Shared with me" is
// the same distinction the legacy app draws client-side from sessionUserRole
// (mapped to userRole here): Personal = projects you own, Shared = everything
// else. So all three tabs are views over one shared, fully cursor-paginated
// "ALL projects" stream (masterState below), each filtering it independently.
function matchesTab(project, tabKey) {
  if (tabKey === PROJECT_CONSTANT.PROJECT_TYPES.PERSONAL) return project.userRole === "OWNER";
  if (tabKey === PROJECT_CONSTANT.PROJECT_TYPES.SHARED_WITH_ME) return project.userRole !== "OWNER";
  return true;
}

function freshMasterState() {
  return { projects: [], cursor: null, hasMore: true };
}

// Module-level singleton — shared by the Projects page and anything that
// needs to silently refresh it (e.g. after creating a project) without a
// full page reload.
const masterState = reactive(freshMasterState());
// Only one underlying fetch in flight at a time — concurrent tabs that both
// need more data await the same request instead of racing separate ones.
let masterFetchPromise = null;
let lastSortBy = "LAST_MODIFIED";
let lastSortDirection = "DESC";

const state = reactive(Object.fromEntries(TAB_KEYS.map((key) => [key, {
  projects: [], hasMore: true, isLoading: false, isLoadingMore: false, hasLoadedOnce: false,
}])));

function recomputeAllTabs() {
  for (const key of TAB_KEYS) {
    state[key].projects = masterState.projects.filter((p) => matchesTab(p, key));
  }
}

async function loadMoreMaster() {
  if (!masterState.hasMore) return;
  if (masterFetchPromise) return masterFetchPromise;
  masterFetchPromise = (async () => {
    try {
      const res = await projectService.getProjects({
        projectsCursor: masterState.cursor,
        sortBy: lastSortBy,
        sortDirection: lastSortDirection,
      });
      masterState.projects.push(...(res.projects ?? []));
      masterState.cursor = res.endCursor;
      masterState.hasMore = res.hasNextPage;
      recomputeAllTabs();
    } finally {
      masterFetchPromise = null;
    }
  })();
  return masterFetchPromise;
}

export function useProjectsByTab() {
  async function loadFirstPage(tabKey) {
    const s = state[tabKey];
    if (s.hasLoadedOnce) return;
    s.hasLoadedOnce = true;
    s.isLoading = true;
    try {
      await loadMore(tabKey);
    } finally {
      s.isLoading = false;
    }
  }

  // Pulls additional pages from the shared underlying ALL stream until this
  // tab has gained a full page of matching projects, or the stream is dry.
  async function loadMore(tabKey) {
    const s = state[tabKey];
    if (!s.hasMore || s.isLoadingMore) return;
    s.isLoadingMore = true;
    try {
      const targetCount = s.projects.length + PROJECT_CONSTANT.PROJECTS_PER_REQUEST;
      while (s.projects.length < targetCount && masterState.hasMore) {
        await loadMoreMaster();
      }
    } finally {
      s.hasMore = masterState.hasMore;
      s.isLoadingMore = false;
    }
  }

  // Silently reset and re-fetch from scratch (no loading flash, no reload) —
  // used after creating a project, since the mutation doesn't return an id we
  // could otherwise just splice into the cached list.
  async function refreshAll() {
    Object.assign(masterState, freshMasterState());
    for (const key of TAB_KEYS) {
      state[key].projects = [];
      state[key].hasMore = true;
      state[key].hasLoadedOnce = false;
    }
    await Promise.all(TAB_KEYS.map(loadFirstPage));
  }

  // Sort is applied server-side against the shared master stream, so changing
  // it invalidates every tab's cached page (not just the active one) and
  // re-fetches from the first page under the new order.
  async function resort(tabKey, sortBy, sortDirection) {
    lastSortBy = sortBy;
    lastSortDirection = sortDirection;
    Object.assign(masterState, freshMasterState());
    for (const key of TAB_KEYS) {
      state[key].projects = [];
      state[key].hasMore = true;
      state[key].hasLoadedOnce = false;
    }
    await loadFirstPage(tabKey);
  }

  // Deleting/adding a document doesn't refetch this cached stream, so the
  // caller (e.g. Project.vue after a delete) patches the affected project's
  // count directly instead of it staying stale until a reload. Every tab's
  // filtered `projects` array holds the same object references as
  // masterState.projects (see recomputeAllTabs), so mutating it here is
  // enough — no re-filtering needed for a count-only change.
  function adjustDocCount(projectId, delta) {
    const p = masterState.projects.find((p) => p.id === projectId);
    if (p) p.totalDocuments = Math.max(0, (p.totalDocuments ?? 0) + delta);
  }

  return { state, loadFirstPage, loadMore, refreshAll, resort, adjustDocCount };
}
